import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { User, Lock, Eye, EyeOff, Loader2, CheckCircle, Mail, Calendar, Crown, Shield } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

export const AccountPage = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    // Redirect if not logged in
    if (!user) {
        navigate('/login');
        return null;
    }

    const handleChangePassword = async (e) => {
        e.preventDefault();
        
        if (!currentPassword || !newPassword || !confirmPassword) {
            toast.error('Veuillez remplir tous les champs');
            return;
        }
        
        if (newPassword.length < 6) {
            toast.error('Le nouveau mot de passe doit contenir au moins 6 caractères');
            return;
        }
        
        if (newPassword !== confirmPassword) {
            toast.error('Les mots de passe ne correspondent pas');
            return;
        }
        
        if (currentPassword === newPassword) {
            toast.error('Le nouveau mot de passe doit être différent de l\'ancien');
            return;
        }
        
        setLoading(true);
        try {
            await api.changePassword(currentPassword, newPassword);
            setSuccess(true);
            toast.success('Mot de passe modifié avec succès!');
            // Clear form
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            // Reset success after 3 seconds
            setTimeout(() => setSuccess(false), 3000);
        } catch (error) {
            console.error('Change password error:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors du changement de mot de passe');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('fr-CA', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const isPremium = user.subscription_tier === 'premium';

    return (
        <div className="min-h-screen py-12 px-4">
            <div className="max-w-2xl mx-auto">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="w-20 h-20 bg-gradient-cameleon rounded-2xl flex items-center justify-center mx-auto mb-4 border-2 border-foreground shadow-pop">
                        <User className="w-10 h-10 text-white" />
                    </div>
                    <h1 className="font-heading text-3xl font-black text-foreground mb-2">Mon Compte</h1>
                    <p className="text-muted-foreground">Gérez vos informations et paramètres</p>
                </div>

                {/* Account Info Card */}
                <div className="bg-white rounded-3xl border-2 border-foreground shadow-[6px_6px_0px_0px_#1e293b] p-6 mb-6">
                    <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
                        <Shield className="w-5 h-5 text-primary" />
                        Informations du compte
                    </h2>
                    
                    <div className="space-y-4">
                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                <User className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Nom</p>
                                <p className="font-semibold">{user.name}</p>
                            </div>
                        </div>
                        
                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                                <Mail className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Email</p>
                                <p className="font-semibold">{user.email}</p>
                            </div>
                        </div>
                        
                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                                <Crown className="w-5 h-5 text-purple-600" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Abonnement</p>
                                <div className="flex items-center gap-2">
                                    <p className="font-semibold">
                                        {isPremium ? 'Premium' : 'Gratuit'}
                                    </p>
                                    {isPremium && (
                                        <span className="px-2 py-0.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs rounded-full">
                                            Actif
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        
                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                                <Calendar className="w-5 h-5 text-amber-600" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Membre depuis</p>
                                <p className="font-semibold">{formatDate(user.created_at)}</p>
                            </div>
                        </div>
                    </div>

                    {!isPremium && (
                        <div className="mt-4 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-200">
                            <p className="text-sm text-purple-800 mb-2">
                                <strong>Passez à Premium</strong> pour accéder au Pack Hebdomadaire complet et aux suggestions IA!
                            </p>
                            <Button 
                                onClick={() => navigate('/subscription')}
                                className="bg-gradient-to-r from-purple-500 to-pink-500 text-white"
                                size="sm"
                            >
                                <Crown className="w-4 h-4 mr-2" />
                                Découvrir Premium
                            </Button>
                        </div>
                    )}
                </div>

                {/* Change Password Card */}
                <div className="bg-white rounded-3xl border-2 border-foreground shadow-[6px_6px_0px_0px_#1e293b] p-6">
                    <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
                        <Lock className="w-5 h-5 text-primary" />
                        Changer le mot de passe
                    </h2>
                    
                    {success && (
                        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3">
                            <CheckCircle className="w-5 h-5 text-green-600" />
                            <p className="text-green-800">Mot de passe modifié avec succès!</p>
                        </div>
                    )}
                    
                    <form onSubmit={handleChangePassword} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="currentPassword" className="font-bold">Mot de passe actuel</Label>
                            <div className="relative">
                                <Input
                                    id="currentPassword"
                                    type={showCurrentPassword ? 'text' : 'password'}
                                    placeholder="••••••••"
                                    value={currentPassword}
                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                    className="rounded-xl border-2 border-gray-200 py-5 pr-12 focus:border-primary focus:shadow-pop transition-all"
                                    data-testid="current-password-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                                >
                                    {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                </button>
                            </div>
                        </div>
                        
                        <div className="space-y-2">
                            <Label htmlFor="newPassword" className="font-bold">Nouveau mot de passe</Label>
                            <div className="relative">
                                <Input
                                    id="newPassword"
                                    type={showNewPassword ? 'text' : 'password'}
                                    placeholder="••••••••"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    className="rounded-xl border-2 border-gray-200 py-5 pr-12 focus:border-primary focus:shadow-pop transition-all"
                                    data-testid="new-password-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                                >
                                    {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                </button>
                            </div>
                            <p className="text-xs text-muted-foreground">Minimum 6 caractères</p>
                        </div>
                        
                        <div className="space-y-2">
                            <Label htmlFor="confirmPassword" className="font-bold">Confirmer le nouveau mot de passe</Label>
                            <div className="relative">
                                <Input
                                    id="confirmPassword"
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    placeholder="••••••••"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className="rounded-xl border-2 border-gray-200 py-5 pr-12 focus:border-primary focus:shadow-pop transition-all"
                                    data-testid="confirm-password-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                                >
                                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                </button>
                            </div>
                        </div>
                        
                        <Button
                            type="submit"
                            disabled={loading}
                            className="w-full btn-pop bg-primary text-white py-5"
                            data-testid="change-password-submit-btn"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                    Modification...
                                </>
                            ) : (
                                <>
                                    <Lock className="w-5 h-5 mr-2" />
                                    Changer le mot de passe
                                </>
                            )}
                        </Button>
                    </form>
                </div>

                {/* Logout Button */}
                <div className="mt-6 text-center">
                    <Button
                        variant="outline"
                        onClick={() => {
                            logout();
                            navigate('/');
                            toast.success('Déconnexion réussie');
                        }}
                        className="border-2 border-red-300 text-red-600 hover:bg-red-50"
                        data-testid="logout-btn"
                    >
                        Se déconnecter
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default AccountPage;
